/**
 * Sidebar — primary navigation surface for the authenticated app.
 *
 * Layout:
 *   - Desktop (≥ 768px): fixed left column, ~14rem wide, always visible.
 *   - Mobile: hidden by default; rendered as a slide-out overlay when
 *     the hamburger toggle in AppLayout sets `open={true}`. Clicking
 *     the backdrop or selecting any link closes it.
 *
 * Items are role-gated against the active route surface. We do NOT
 * render placeholder links to features that don't exist (Team,
 * Settings, Reports) — those land in their own PRs (CLAUDE.md rule 14).
 *
 * Why a separate component and not inline in AppLayout: AppLayout's
 * responsibilities (auth gating, realtime subscriptions, idle timeout,
 * offline banner) are orthogonal to nav. Splitting keeps each under
 * the 600-line cap and lets nav-only tests target Sidebar directly.
 */

import { type ReactNode } from 'react'
import { NavLink }         from 'react-router'
import { useTranslation }  from 'react-i18next'
import { useBrand }        from '@/lib/brand/BrandProvider'
import { useRole, useSignOut } from '@/hooks/useAuth'
import { useTenantFeatures }    from '@/hooks/useTenantFeatures'
import { LanguageSwitcher }     from '@/components/settings/LanguageSwitcher'
import { OrgSwitcher }          from '@/components/layout/OrgSwitcher'
import { RoleSwitcher }         from '@/components/layout/RoleSwitcher'
import { PATHS }                from '@/router/paths'
import { useCurrentCampusStore } from '@/store/currentCampusStore'

interface SidebarProps {
  /** Open state on mobile. Ignored on desktop (sidebar is always visible). */
  open:    boolean
  onClose: () => void
}

const ADMIN_ROLES = new Set(['super_admin', 'contractor_admin'])
const SUPER_ADMIN_ONLY = new Set(['super_admin'])
const SUPERVISOR_PLUS = new Set(['supervisor', 'super_admin', 'contractor_admin'])
const BOARD_ROLES = new Set(['supervisor', 'super_admin', 'contractor_admin', 'client_view', 'maintenance'])
const FEE_REQUEST_ROLES = new Set(['super_admin', 'contractor_admin', 'owner_approver'])

export function Sidebar({ open, onClose }: SidebarProps): ReactNode {
  const { t } = useTranslation()
  const brand   = useBrand()
  const role    = useRole() ?? ''
  const signOut = useSignOut()
  // PROJECT_STATE task P2 #11. Published by SupervisorDashboard's
  // URL-bound campus filter; renders only when set.
  const campusName = useCurrentCampusStore((s) => s.campusName)

  const { has: hasFeature } = useTenantFeatures()
  const canSeeBoard        = BOARD_ROLES.has(role)
  const canCreateTasks     = SUPERVISOR_PLUS.has(role)
  const canSeeTeam         = SUPERVISOR_PLUS.has(role)
  const canSeeAnomalies    = SUPERVISOR_PLUS.has(role)
  const canSeeCorrections  = ADMIN_ROLES.has(role)
  const canSeeReports      = ADMIN_ROLES.has(role)
  // Bidding tool: owner/admin only AND tenant-flagged (rolled out per
  // tenant, demo org first). Exposes internal cost basis + margin, so it
  // never reaches owner_approver / supervisor / cleaner surfaces.
  const canSeeBidTool      = ADMIN_ROLES.has(role) && hasFeature('bidding_tool')
  // Leads are platform-wide marketing inquiries across every vertical —
  // super_admin only (a tenant's contractor_admin must not see them).
  const canSeeLeads        = SUPER_ADMIN_ONLY.has(role)
  // Fee requests nav requires BOTH the role AND the tenant flag —
  // tenants whose contract doesn't include the exceedance-fee clause
  // never see the link even if their owner_approver role exists.
  const canSeeFeeRequests  = FEE_REQUEST_ROLES.has(role) && hasFeature('additional_fee_workflow')
  // Abandoned-belongings queue: same role set as Team (supervisor+).
  // Tenant flag mirrors the cleaner-side affordance — tenants whose
  // contract does not include the clause never see the link.
  const canSeeAbandoned    = SUPERVISOR_PLUS.has(role) && hasFeature('abandoned_belongings_tracking')

  return (
    <>
      {/* Backdrop — visible only when open on mobile */}
      <div
        data-testid="sidebar-backdrop"
        onClick={onClose}
        style={{
          display:    open ? 'block' : 'none',
          position:   'fixed',
          inset:      0,
          background: 'rgba(0,0,0,0.4)',
          zIndex:     40,
        }}
        className="rro-sidebar-backdrop"
      />

      <aside
        data-testid="sidebar"
        data-open={open ? 'true' : 'false'}
        className="rro-sidebar"
        aria-label={t('nav.primary', 'Primary navigation')}
        style={{
          width:          '14rem',
          background:     'var(--brand-white)',
          borderRight:    '1px solid var(--brand-neutral-300)',
          display:        'flex',
          flexDirection:  'column',
          position:       'fixed',
          top:            0,
          left:           open ? 0 : '-14rem',
          transition:     'left 0.2s ease',
          zIndex:         50,
        }}
      >
        <div
          style={{
            display:      'flex',
            alignItems:   'center',
            gap:          '0.5rem',
            padding:      '1rem',
            borderBottom: '1px solid var(--brand-neutral-100)',
          }}
        >
          {brand.assets.logoMarkUrl && (
            <img
              src={brand.assets.logoMarkUrl}
              alt=""
              width={32}
              height={32}
              style={{ borderRadius: 'var(--brand-radius-sm)', objectFit: 'contain' }}
            />
          )}
          <span data-testid="sidebar-brand-name" style={{ display: 'inline-flex', alignItems: 'center' }}>
            <OrgSwitcher variant="full" fallback={brand.name} />
          </span>
        </div>

        <div style={{ marginTop: '0.6rem' }}>
          <RoleSwitcher variant="full" />
        </div>

        {campusName && (
          <div
            data-testid="sidebar-current-campus"
            style={{
              display:    'flex',
              alignItems: 'center',
              gap:        '0.4rem',
              padding:    '0.5rem 1rem',
              fontSize:   '0.75rem',
              color:      'var(--brand-neutral-700)',
              background: 'var(--brand-primary-subtle)',
              borderBottom: '1px solid var(--brand-neutral-100)',
            }}
          >
            <span style={{ color: 'var(--brand-neutral-500)' }}>
              {t('nav.viewingCampus', 'Viewing:')}
            </span>
            <strong
              data-testid="sidebar-current-campus-name"
              style={{ color: 'var(--brand-primary)' }}
            >
              {campusName}
            </strong>
          </div>
        )}

        <nav style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.15rem', padding: '0.6rem' }}>
          <SidebarLink to={PATHS.dashboard} label={t('nav.dashboard', 'Dashboard')} testId="nav-dashboard" onClose={onClose} />
          {canSeeBoard && (
            <SidebarLink to={PATHS.roomBoard} label={t('nav.rooms', 'Rooms')} testId="nav-rooms" onClose={onClose} />
          )}
          {canCreateTasks && (
            <SidebarLink to={PATHS.taskCreate} label={t('nav.newTask', '+ New task')} testId="nav-new-task" onClose={onClose} accent />
          )}
          {canCreateTasks && (
            <SidebarLink to={PATHS.taskBulk} label={t('nav.bulkNewTask', 'Bulk create')} testId="nav-bulk-new-task" onClose={onClose} />
          )}
          {canSeeTeam && (
            <SidebarLink to={PATHS.team} label={t('nav.team', 'Team')} testId="nav-team" onClose={onClose} />
          )}
          {canSeeAnomalies && (
            <SidebarLink to={PATHS.anomalies} label={t('nav.anomalies', 'Anomalies')} testId="nav-anomalies" onClose={onClose} />
          )}
          {canSeeFeeRequests && (
            <SidebarLink to={PATHS.feeRequests} label={t('nav.feeRequests', 'Fee requests')} testId="nav-fee-requests" onClose={onClose} />
          )}
          {canSeeAbandoned && (
            <SidebarLink to={PATHS.abandonedBelongings} label={t('nav.abandonedBelongings', 'Abandoned belongings')} testId="nav-abandoned-belongings" onClose={onClose} />
          )}
          {canSeeReports && (
            <SidebarLink to={PATHS.reports} label={t('nav.reports', 'Reports')} testId="nav-reports" onClose={onClose} />
          )}
          {canSeeBidTool && (
            <SidebarLink to={PATHS.bidTool} label={t('nav.bidTool', 'Bid tool')} testId="nav-bid-tool" onClose={onClose} />
          )}
          {canSeeLeads && (
            <SidebarLink to={PATHS.leads} label={t('nav.leads', 'Leads')} testId="nav-leads" onClose={onClose} />
          )}
          {canSeeCorrections && (
            <SidebarLink to={PATHS.corrections} label={t('nav.corrections', 'MCP corrections')} testId="nav-corrections" onClose={onClose} />
          )}
          {canSeeCorrections && (
            <SidebarLink to={PATHS.orgSettings} label={t('nav.orgSettings', 'Settings')} testId="nav-org-settings" onClose={onClose} />
          )}
        </nav>

        <footer
          style={{
            display:       'flex',
            flexDirection: 'column',
            gap:           '0.5rem',
            padding:       '0.75rem',
            borderTop:     '1px solid var(--brand-neutral-100)',
          }}
        >
          <LanguageSwitcher />
          <button
            type="button"
            onClick={() => { void signOut() }}
            data-testid="sidebar-signout"
            style={{
              padding:      '0.5rem 0.7rem',
              background:   'transparent',
              color:        'var(--brand-neutral-700)',
              border:       '1px solid var(--brand-neutral-300)',
              borderRadius: 'var(--brand-radius-md)',
              fontSize:     '0.85rem',
              cursor:       'pointer',
              textAlign:    'left',
            }}
          >
            {t('auth.signOut')}
          </button>
          <p
            data-testid="sidebar-attribution"
            style={{
              margin:    0,
              fontSize:  '0.7rem',
              color:     'var(--brand-neutral-500)',
              textAlign: 'center',
              lineHeight: 1.4,
            }}
          >
            Envisioned by{' '}
            <a
              href="https://envisionvirtualedge.org"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="sidebar-attribution-link"
              style={{ color: 'var(--brand-neutral-700)', textDecoration: 'underline' }}
            >
              Envision VirtualEdge Group, LLC™
            </a>{' '}
            · © {new Date().getFullYear()}
          </p>
        </footer>
      </aside>
    </>
  )
}

interface SidebarLinkProps {
  to:       string
  label:    string
  testId:   string
  accent?:  boolean
  onClose:  () => void
}

function SidebarLink({ to, label, testId, accent, onClose }: SidebarLinkProps): ReactNode {
  return (
    <NavLink
      to={to}
      data-testid={testId}
      onClick={onClose}
      end={to === PATHS.dashboard}
      style={({ isActive }) => ({
        display:       'block',
        padding:       '0.5rem 0.7rem',
        borderRadius:  'var(--brand-radius-sm)',
        fontSize:      '0.9rem',
        textDecoration:'none',
        color:         accent
          ? 'var(--brand-white)'
          : (isActive ? 'var(--brand-primary)' : 'var(--brand-neutral-700)'),
        background:    accent
          ? 'var(--brand-primary)'
          : (isActive ? 'var(--brand-primary-subtle)' : 'transparent'),
        fontWeight:    isActive || accent ? 600 : 400,
      })}
    >
      {label}
    </NavLink>
  )
}
